import React, { useState, useEffect } from 'react';
import { usePerformance } from '../hooks/usePerformance';

interface TestResult {
  name: string;
  status: 'pass' | 'fail' | 'warning';
  message: string;
}

/**
 * Comprehensive test suite for responsive design and integration testing
 * Tests various screen sizes, device capabilities, and user workflows
 */
export const ResponsiveTestSuite: React.FC = () => {
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const { deviceInfo, isLowEndDevice, isMobile } = usePerformance();

  const runTests = async () => {
    setIsRunning(true);
    const results: TestResult[] = [];

    try {
      // Test 1: Screen size detection
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      
      if (screenWidth >= 320 && screenHeight >= 568) {
        results.push({
          name: 'Минимальный размер экрана',
          status: 'pass',
          message: `Экран ${screenWidth}x${screenHeight} поддерживается`
        });
      } else {
        results.push({
          name: 'Минимальный размер экрана',
          status: 'fail',
          message: `Экран ${screenWidth}x${screenHeight} слишком мал`
        });
      }

      // Test 2: Mobile detection
      results.push({
        name: 'Определение мобильного устройства',
        status: isMobile ? 'pass' : 'warning',
        message: isMobile ? 'Мобильное устройство обнаружено' : 'Десктопное устройство'
      });

      // Test 3: Touch support
      const hasTouchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      results.push({
        name: 'Поддержка сенсорного ввода',
        status: hasTouchSupport ? 'pass' : 'warning',
        message: hasTouchSupport ? 'Сенсорный ввод поддерживается' : 'Только мышь и клавиатура'
      });

      // Test 4: Telegram WebApp API
      const hasTelegramWebApp = typeof window.Telegram !== 'undefined' && window.Telegram.WebApp;
      results.push({
        name: 'Telegram WebApp API',
        status: hasTelegramWebApp ? 'pass' : 'warning',
        message: hasTelegramWebApp ? 'Telegram WebApp API доступен' : 'Запущено вне Telegram'
      });

      // Test 5: Local Storage (simplified)
      try {
        const testKey = 'responsive-test-' + Date.now();
        localStorage.setItem(testKey, 'test');
        localStorage.removeItem(testKey);
        results.push({
          name: 'Local Storage',
          status: 'pass',
          message: 'Local Storage работает'
        });
      } catch (error) {
        results.push({
          name: 'Local Storage',
          status: 'fail',
          message: 'Local Storage недоступен'
        });
      }

      // Test 6: Basic CSS support
      results.push({
        name: 'CSS поддержка',
        status: 'pass',
        message: 'Базовая поддержка CSS работает'
      });

      // Test 7: Performance characteristics (simplified)
      results.push({
        name: 'Производительность устройства',
        status: isLowEndDevice ? 'warning' : 'pass',
        message: isLowEndDevice ? 'Слабое устройство - включены оптимизации' : 'Хорошая производительность'
      });

      // Test 8: Viewport meta tag
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      results.push({
        name: 'Viewport meta тег',
        status: viewportMeta ? 'pass' : 'warning',
        message: viewportMeta ? 'Viewport настроен' : 'Viewport meta тег отсутствует'
      });

    } catch (error) {
      results.push({
        name: 'Общая ошибка тестирования',
        status: 'fail',
        message: 'Произошла ошибка при выполнении тестов'
      });
    }

    setTestResults(results);
    setIsRunning(false);
  };

  useEffect(() => {
    runTests();
  }, []);

  const getStatusIcon = (status: TestResult['status']) => {
    switch (status) {
      case 'pass': return '✅';
      case 'fail': return '❌';
      case 'warning': return '⚠️';
    }
  };

  const getStatusColor = (status: TestResult['status']) => {
    switch (status) {
      case 'pass': return 'text-green-600';
      case 'fail': return 'text-red-600';
      case 'warning': return 'text-yellow-600';
    }
  };

  const passCount = testResults.filter(r => r.status === 'pass').length;
  const failCount = testResults.filter(r => r.status === 'fail').length;
  const warningCount = testResults.filter(r => r.status === 'warning').length;

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">🧪 Тестирование системы</h1>
        <p className="text-tg-secondary">
          Проверка совместимости и производительности
        </p>
      </div>

      {/* Test Summary */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold mb-4">Сводка результатов</h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{passCount}</div>
            <div className="text-sm text-tg-secondary">Пройдено</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">{warningCount}</div>
            <div className="text-sm text-tg-secondary">Предупреждения</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{failCount}</div>
            <div className="text-sm text-tg-secondary">Ошибки</div>
          </div>
        </div>
      </div>

      {/* Device Information */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold mb-4">Информация об устройстве</h2>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-tg-secondary">Экран:</span>
            <span>{window.innerWidth} × {window.innerHeight}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-tg-secondary">User Agent:</span>
            <span className="text-right max-w-xs truncate">{navigator.userAgent}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-tg-secondary">Платформа:</span>
            <span>{navigator.platform}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-tg-secondary">Язык:</span>
            <span>{navigator.language}</span>
          </div>
          {deviceInfo.cores && (
            <div className="flex justify-between">
              <span className="text-tg-secondary">Ядра процессора:</span>
              <span>{deviceInfo.cores}</span>
            </div>
          )}
          {deviceInfo.memory && (
            <div className="flex justify-between">
              <span className="text-tg-secondary">Память:</span>
              <span>{deviceInfo.memory} GB</span>
            </div>
          )}
        </div>
      </div>

      {/* Test Results */}
      <div className="card mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Результаты тестов</h2>
          <button 
            onClick={runTests}
            disabled={isRunning}
            className="btn-secondary text-sm"
          >
            {isRunning ? 'Тестирование...' : 'Повторить тесты'}
          </button>
        </div>
        
        <div className="space-y-3">
          {testResults.map((result, index) => (
            <div key={index} className="flex items-start space-x-3 p-3 bg-tg-primary rounded-lg">
              <span className="text-lg">{getStatusIcon(result.status)}</span>
              <div className="flex-1">
                <div className="font-medium">{result.name}</div>
                <div className={`text-sm ${getStatusColor(result.status)}`}>
                  {result.message}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Responsive Design Test */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold mb-4">Тест адаптивного дизайна</h2>
        <div className="space-y-4">
          {/* Mobile layout test */}
          <div className="sm:hidden">
            <div className="text-green-600 font-medium">✅ Мобильная версия активна</div>
            <p className="text-sm text-tg-secondary">Интерфейс оптимизирован для мобильных устройств</p>
          </div>
          
          {/* Tablet layout test */}
          <div className="hidden sm:block md:hidden">
            <div className="text-green-600 font-medium">✅ Планшетная версия активна</div>
            <p className="text-sm text-tg-secondary">Интерфейс адаптирован для планшетов</p>
          </div>
          
          {/* Desktop layout test */}
          <div className="hidden md:block">
            <div className="text-yellow-600 font-medium">⚠️ Десктопная версия активна</div>
            <p className="text-sm text-tg-secondary">Приложение предназначено для мобильных устройств</p>
          </div>

          {/* Grid test */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
              <div key={num} className="bg-tg-button text-tg-button text-center py-2 rounded text-sm">
                {num}
              </div>
            ))}
          </div>
          <p className="text-sm text-tg-secondary">
            Сетка должна показывать: 2 колонки на мобильном, 3 на планшете, 4 на десктопе
          </p>
        </div>
      </div>

      {/* Performance Test */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Тест производительности</h2>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span>Время загрузки DOM:</span>
            <span className="font-mono text-sm">
              {performance.timing ? 
                `${performance.timing.domContentLoadedEventEnd - performance.timing.navigationStart}ms` : 
                'Недоступно'
              }
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span>Использование памяти:</span>
            <span className="font-mono text-sm">
              {(performance as any).memory ? 
                `${Math.round((performance as any).memory.usedJSHeapSize / 1024 / 1024)}MB` : 
                'Недоступно'
              }
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span>Оптимизации включены:</span>
            <span className={isLowEndDevice ? 'text-yellow-600' : 'text-green-600'}>
              {isLowEndDevice ? 'Да (слабое устройство)' : 'Нет (хорошая производительность)'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResponsiveTestSuite;